import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import type { Locale } from '@/strings/types';
import { useLocale } from '@/hooks/useLocale';
import { appSignupUrl } from '@/config/appLinks';
import { HERO_EN } from '@/copy/hero-en';
import { HERO_AR } from '@/copy/hero-ar';
import { PRI_WORDS } from '@/copy/hero-pri-words';
import Typewriter from './Typewriter';
import TypewriterAR from './TypewriterAR';
import Waveform from './Waveform';
import TabRow from './TabRow';
import DemoNav from './DemoNav';
import AppShell, {
  type NavItem,
  type SectionItem,
  type ShellHeader,
} from '@/components/AppShell/AppShell';
import MinutesView from '@/components/AppShell/MinutesView';
import TranscriptView from '@/components/AppShell/TranscriptView';
import HomeView from '@/components/AppShell/HomeView';
import ActionsView, { ActionsTouchDot } from '@/components/AppShell/ActionsView';
import LibraryView, {
  LibraryDialog,
  LibraryTouchDot,
} from '@/components/AppShell/LibraryView';
import {
  RecorderDock,
  RecorderTopButton,
  RecorderTouchDot,
} from '@/components/AppShell/Recorder';
import {
  RecorderDemoProvider,
  useRecorderDemo,
} from '@/components/AppShell/useRecorderDemo';
import {
  ActionsDemoProvider,
  useActionsDemo,
} from '@/components/AppShell/useActionsDemo';
import {
  LibraryDemoProvider,
  useLibraryDemo,
} from '@/components/AppShell/useLibraryDemo';
import { useInViewOnce } from '@/hooks/useInViewOnce';
import './Hero.css';

/* 9s (owner, 2026-09-08), from 6s, itself up from 4.5s. Drives both the
   auto-advance interval and the progress fill's animation-duration, which TabRow
   sets inline from this — they cannot drift apart.

   This applies to tabs 1 and 2, the two with no scripted sequence: the minutes
   summary and the transcript are both READ rather than watched, and 6s was not
   a paragraph's worth of time. The full cycle goes 53.5s -> 59.5s. */
const TAB_CYCLE_MS = 9000;

/* Per-tab override, for the tabs carrying a TIMED sequence.

   Tab 3's recorder plays record -> 3s -> stop -> ready, landing at 5.35s, so at
   the shared 6s the finished state would flash for half a second and vanish —
   the beat the whole tab exists to show. 9s gives it ~3.6s to be read (owner,
   2026-09-06).

   Tab 4 runs longest: complete two items -> fill in an owner -> filter by
   project is six presses, and at the first cut's pace (one every 0.84s, each
   fired before the dot had finished travelling) it read as frantic — "too fast,
   user might get overwhelmed" (owner, 2026-09-06). The script now lets the dot
   arrive before it presses and leaves each result on screen to be read, which
   puts the last press at 11.30s; 15s keeps a ~3.2s hold on the filtered list.
   The timing itself lives in useActionsDemo's T — change both together.

   Tab 5 runs FOUR beats, and is the longest for that reason: the cursor picks a
   ready-made template (press at 1.5s) and reads it, changes its mind and opens
   the composer (5.1s), then PRESSES each of the eight catalog rows in turn
   (last landing at 11.15s), and finally Save & Publish (12.95s), with the new
   card landing in the library at 13.65s.

   17.5s, up from 16.5 on 2026-09-07. The owner asked for the catalog picks to
   be visibly clicked rather than ticking themselves, which turned eight state
   changes into eight aim-travel-press cycles and cost ~1.25s. The extra second
   is bought back for the FINAL FRAME rather than the sequence: at 16.5s the
   finished library would have held for 2.85s, and that frame is the whole
   argument the tab is making. Now ~3.85s.

   The timing lives in useLibraryDemo's T — change both together. */
/* ⚠️ Tab 3's 9000 now EQUALS TAB_CYCLE_MS and is kept deliberately, not left
   behind. The two numbers are equal by coincidence, not by derivation: the base
   is "how long a tab of prose wants" and this one is "the recorder's sequence
   lands at 5.35s and needs ~3.6s after it". Delete the entry and the next change
   to the base silently retimes a scripted beat. */
const TAB_CYCLE_OVERRIDE: Readonly<Record<number, number>> = {
  2: 9000,
  3: 15000,
  4: 17500,
};
const cycleFor = (i: number) => TAB_CYCLE_OVERRIDE[i] ?? TAB_CYCLE_MS;

/* How long a hover may hold ONE tab, on top of that tab's own dwell.
   A budget, not a reset: it drains while the pointer is over the demo, does NOT
   refill when the pointer leaves and comes back, and is restored only when the
   tab changes. So a tab's ceiling is its dwell + 5s — however many times the
   pointer crosses in and out.

   5s, halved from 10s (owner, 2026-09-08). It is a smaller cut than it looks:
   the dwells it sits on top of went up in the same pass, so tab 1's ceiling
   moved 16s -> 14s while its unhovered dwell went 6s -> 9s. The hold is now
   mostly in the dwell, where every visitor gets it, rather than in a bonus only
   a moving cursor earns.

   Owner, 2026-09-06: an indefinite hover pause "might [make a user who] zoned
   out think this is stuck", and the visitor is on the page for a limited time,
   so seeing all five tabs beats dwelling on one.

   ACCESSIBILITY NOTE, recorded because it was a deliberate trade and not an
   oversight: WCAG 2.2 SC 2.2.2 (Pause, Stop, Hide, Level A) wants a mechanism
   to pause auto-updating content that runs past 5s, and a budget that expires
   is not one. Two uncapped mechanisms were proposed alongside this — pause on
   keyboard focus, and no auto-advance under prefers-reduced-motion — and the
   owner deferred both pending usage feedback. Revisit together.
   The movement gate below narrows this further, which is noted there. */
const HOVER_BUDGET_MS = 5000;

/**
 * How far the pointer must travel over the demo before THIS tab is granted its
 * hover budget (px of path, reset on every tab change).
 *
 * A PARKED CURSOR NO LONGER PAUSES ANYTHING (owner, 2026-09-07). The budget was
 * granted to every tab on nothing more than the pointer being inside the zone,
 * so someone who left the mouse over the demo and looked away silently added the
 * whole budget to all five tabs — a carousel held open by a cursor nobody was
 * driving. The budget should reach "only the tabs the user is genuinely
 * interested in and is actively moving his cursor through".
 *
 * So the grant is per-tab and movement-triggered: move on this tab and it gets
 * its full HOVER_BUDGET_MS; sit still and it gets none. Once granted it STAYS granted for
 * that tab — the owner's "even if just once" — because someone who moved to a
 * row and then stopped to read is exactly who the pause is for. Sustained
 * movement is not the signal; intent is, and one deliberate move carries it.
 *
 * Why a threshold rather than any pointermove: entering at the very edge of the
 * zone, a sub-pixel tremor, or a hand resting on a trackpad all emit moves with
 * nobody driving. 24px is about one row's height and far below any real reading
 * gesture, so it separates "moved" from "twitched" without being felt.
 *
 * What this deliberately does NOT catch, and why that is right: scrolling the
 * page under a stationary cursor fires pointerenter but no movement, so a demo
 * sliding under a parked pointer grants nothing. That is the case being fixed,
 * not a gap in the fix.
 */
const HOVER_INTENT_PX = 24;

/* Live/preview toggle for the interactive tab + mockup demo.

   ON as of 2026-09-07 — the demo is LIVE on prinotes.com. It was off on main
   through development, with the `hero-demo` and `app-shell-mockup` branches
   flipping it true so the demo rendered at their pages.dev preview URLs; that
   is history now, and the flag's purpose from here is a kill switch. Setting it
   false removes the tab row and the shell from the homepage and nothing else —
   the disclosure band below it is composed in routes/index.tsx and does not
   depend on this. */
const SHOW_HERO_DEMO = true;

/* A union rather than optional fields, mirroring AppShellProps: a top-level
   'page' tab has no meeting and no section, and a 'meeting' tab must have both.
   'recorder' and 'actions' are both pages; they are separate kinds because each
   brings its own demo provider, not because the shell differs. */
type TabView =
  | {
      kind: 'meeting';
      nav: NavItem;
      section: SectionItem;
      header: Record<Locale, ShellHeader>;
      view: ReactNode;
    }
  | { kind: 'recorder'; nav: NavItem; view: ReactNode }
  | { kind: 'actions'; nav: NavItem; view: ReactNode }
  | { kind: 'library'; nav: NavItem; view: ReactNode };

/**
 * Tab 3's shell. A component of its own PURELY so `useRecorderDemo` mounts and
 * unmounts with the tab: called up in `Hero` it would start its timers on page
 * load, and the record -> stop -> ready sequence would be long finished before
 * anyone reached tab 3. Mounting is the trigger.
 *
 * It also owns the provider, because the dock and the pop-out are two views of
 * ONE recording session — the same reason the app shares a RecordingContext.
 */
function RecordingShell({
  locale,
  nav,
  children,
}: {
  locale: Locale;
  nav: NavItem;
  children: ReactNode;
}) {
  const rec = useRecorderDemo();
  return (
    <RecorderDemoProvider value={rec}>
      <AppShell
        locale={locale}
        variant="page"
        activeNavItem={nav}
        railRecorder={<RecorderDock />}
        topbarRecorder={<RecorderTopButton />}
        overlay={<RecorderTouchDot />}
      >
        {children}
      </AppShell>
    </RecorderDemoProvider>
  );
}

/**
 * Tab 4's shell. Same reason for existing as RecordingShell: mounting starts the
 * scripted sequence, so it has to mount with the tab rather than with the page.
 *
 * The touch dot goes in `overlay` — above the sidebar AND the pane — because the
 * dot has to reach every control the script presses, and at the widest layout
 * those are spread across the whole body.
 */
function ActionsShell({
  locale,
  nav,
  children,
}: {
  locale: Locale;
  nav: NavItem;
  children: ReactNode;
}) {
  const demo = useActionsDemo();
  return (
    <ActionsDemoProvider value={demo}>
      <AppShell
        locale={locale}
        variant="page"
        activeNavItem={nav}
        overlay={<ActionsTouchDot />}
      >
        {children}
      </AppShell>
    </ActionsDemoProvider>
  );
}

/**
 * Tab 5's shell. Same reason for existing as RecordingShell and ActionsShell:
 * mounting starts the scripted sequence, so it has to mount with the tab rather
 * than with the page.
 *
 * The composer AND the cursor both go in `overlay` — above the sidebar and the
 * top bar. The composer because it is a MODAL and the app's modal scrims the
 * whole window; rendered beside the page it would dim only the content column
 * and read as a panel that appeared, rather than as the dialog the
 * "+ New Template" button just opened. The cursor because its three targets sit
 * in different subtrees: a card in the page, the New Template button in the
 * toolbar, and Save & Publish inside the dialog.
 */
function LibraryShell({
  locale,
  nav,
  children,
}: {
  locale: Locale;
  nav: NavItem;
  children: ReactNode;
}) {
  const demo = useLibraryDemo();
  return (
    <LibraryDemoProvider value={demo}>
      <AppShell
        locale={locale}
        variant="page"
        activeNavItem={nav}
        overlay={
          <>
            <LibraryDialog />
            <LibraryTouchDot />
          </>
        }
      >
        {children}
      </AppShell>
    </LibraryDemoProvider>
  );
}

/* Every tab shows the SAME meeting, so the detail header is shared. A visitor
   who watches two tabs should see one document being worked on, not two
   unrelated screenshots. No em dash in the title, per the locked copy rule.

   THE DATE IS THE SAME ISO STRING IN BOTH LOCALES, matching the app (owner,
   2026-09-07: "keep dates consistent"). The Arabic used to spell the month out
   as «10 سبتمبر 2025» on the reading of style guide §7, but §7 governs dates we
   WRITE, and this is a field the app prints: MoMP's buildHeaderMeta pushes
   `{ icon: 'date', label: m.meeting_date }` — the stored value, unformatted and
   identical in every locale (meetingsDetailLib.ts, and its test pins
   '2026-06-09'). A mockup that reformats what the product does not is claiming
   a feature. */
const MEETING: Record<Locale, ShellHeader> = {
  en: {
    title: 'National Day Activation: Concept Review',
    project: 'No project',
    date: '2025-09-10',
  },
  ar: {
    title: 'فعالية اليوم الوطني: مراجعة التصور',
    project: 'دون مشروع',
    date: '2025-09-10',
  },
};

/* What each hero tab shows inside the app-shell mockup. Index matches the tab
   order in hero-en/hero-ar. Tabs 1-3 are built; 4-5 fall back to tab 1. */
const MINUTES_VIEW = {
  kind: 'meeting',
  nav: 'meetings',
  section: 'minutes',
  header: MEETING,
  view: <MinutesView />,
} as const satisfies TabView;

const TRANSCRIPT_VIEW = {
  kind: 'meeting',
  nav: 'meetings',
  section: 'transcript',
  header: MEETING,
  view: <TranscriptView />,
} as const satisfies TabView;

/* Tab 3 breaks the "same meeting throughout" rule on purpose. Recording is
   something you START, and in the app you start it from Home, before any
   meeting exists — showing it inside an open meeting would put the transport in
   the one place it is not the point. */
const RECORDING_VIEW = {
  kind: 'recorder',
  nav: 'home',
  view: <HomeView />,
} as const satisfies TabView;

/* Tab 4 leaves the meeting too, and for the same kind of reason: /actions is
   the ACROSS-meetings view, so showing it inside one open meeting would
   contradict the claim the tab is making. Its rows include the three action
   items tab 1's minutes produced, sitting alongside items from two other
   meetings — the aggregation demonstrated rather than captioned. */
const ACTIONS_VIEW = {
  kind: 'actions',
  nav: 'actions',
  view: <ActionsView />,
} as const satisfies TabView;

/* Tab 5 REPLACED "Schedules / Calendar Events" (owner, 2026-09-07) — the row
   stays at five, and the calendar is dropped rather than pushed to a sixth.

   It browses a stocked library, opens the composer, and lands the new template
   back in the grid — three beats, because the owner asked for a step in front
   of the dialog "so the user understands that there are templates already and
   they can create more". */
const LIBRARY_VIEW = {
  kind: 'library',
  nav: 'templates',
  view: <LibraryView />,
} as const satisfies TabView;

const TAB_VIEWS: readonly TabView[] = [
  MINUTES_VIEW,
  TRANSCRIPT_VIEW,
  RECORDING_VIEW,
  ACTIONS_VIEW,
  LIBRARY_VIEW,
];

export default function Hero() {
  const { locale, dir } = useLocale();
  const copy = locale === 'ar' ? HERO_AR : HERO_EN;
  const [activeTab, setActiveTab] = useState(0);
  /* The ONLY thing that pauses the cycle. There used to be a `userManual` flag
     beside it, set on click and never cleared — so a single click on any tab
     stopped the carousel for the life of the page (owner, 2026-09-06).
     It was redundant as well as wrong: you have to be over the demo to click a
     tab, so `hovered` already covers the click. Pointer in, paused; pointer out,
     running again from whichever tab was chosen. */
  const [hovered, setHovered] = useState(false);
  /* True once the current tab's hover budget is gone. State, not a ref: the
     carousel has to start moving again the moment it flips. */
  const [budgetSpent, setBudgetSpent] = useState(false);
  /* True once the pointer has actually MOVED over the demo on this tab — see
     HOVER_INTENT_PX. State because the pause turns on the instant it flips. */
  const [intent, setIntent] = useState(false);
  /* Touch only — see handleSelect. Stops rotation for the rest of the visit. */
  const [stopped, setStopped] = useState(false);

  /* How much of the current tab's dwell is left, and how much hover budget is
     left, both in ms. Refs rather than state because nothing renders from
     them — writing one must not re-render, or the timeout it feeds would be
     torn down and rebuilt on its own update. */
  const remainingRef = useRef(cycleFor(0));
  const budgetRef = useRef(HOVER_BUDGET_MS);
  /* Path length walked over the demo on this tab, and the last point seen.
     Refs, not state: this updates on every pointermove, and re-rendering the
     whole hero at pointer frequency to accumulate a number nothing draws would
     be the expensive way to do nothing. Only crossing the threshold renders. */
  const travelRef = useRef(0);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  /* THE CAROUSEL WAITS FOR AN AUDIENCE (owner, 2026-09-08).
     It used to start on mount, and measured at six real viewports the SHELL —
     the thing being demonstrated — is at most 48% on screen at landing and 0%
     on three of them (1440x780, 1024x768, 375x667); only the tab row is
     reliably above the fold. Against dwells of 9/9/9/15/17.5s that meant a
     visitor who read the hero for more than nine seconds had already missed
     tab 1, and typically arrived mid-script on tab 2 or 3. Worse for the
     scripted tabs: tab 4's cursor finishes at 11.75s of its 15s dwell and
     tab 5's at ~14.4s of 17.5s, so arriving in the tail showed the END state —
     boxes ticked, filter applied, no cursor — which is the argument those tabs
     exist to make, invisible.

     Observed on the STAGE rather than the tab row, because the tab row is above
     the fold everywhere and would fire immediately, which is the bug. The stage
     mirrors .pnshell-frame's box exactly (see the note in Hero.css), so this is
     genuinely asking about the app window.

     ⚠️ LOCALE CHANGES THE PAGE POSITION, NOT THE RULE. The Arabic hero is
     shorter — a two-line sub against English's three, plus Plex Sans Arabic's
     own metrics — so the stage sits 17px higher at 1440 and 38px higher at 390.
     That shifts WHERE in the document the trigger fires, and must not shift
     WHETHER it fires sensibly, which is why the margin is a percentage of the
     viewport and the observed box is the stage rather than any fixed offset.

     Tab 1's minutes auto-scroll needs no gate of its own: useAutoScroll already
     runs its rAF loop only while the panel is on screen, and restarts from the
     top on re-entry. Tabs 2-5 mount when their tab activates, which now cannot
     happen before this fires. */
  const stageRef = useRef<HTMLDivElement>(null);
  const started = useInViewOnce(stageRef);

  // D18: Reset the tab when locale toggles — otherwise activeTab leaks across
  // the EN↔AR switch in Storybook globals.
  useEffect(() => {
    setActiveTab(0);
    setStopped(false);
  }, [locale]);

  /* A new tab gets its full dwell AND a fresh hover budget. Declared BEFORE the
     two timer effects so React's ordering does the right thing on a tab change:
     cleanups run first (each debiting whatever it just spent), then effects run
     in declaration order, so this restores both counters before either timer
     reads one. */
  useEffect(() => {
    remainingRef.current = cycleFor(activeTab);
    budgetRef.current = HOVER_BUDGET_MS;
    setBudgetSpent(false);
    /* Each tab has to earn its own budget — see HOVER_INTENT_PX. */
    setIntent(false);
    travelRef.current = 0;
    lastPointRef.current = null;
  }, [activeTab]);

  /* A hover holds the carousel only once this tab has been earned by an actual
     cursor movement, and only while there is budget left to spend. */
  const paused = hovered && intent && !budgetSpent;

  /* Accumulates path length and flips `intent` once, at the threshold. Returns
     early afterwards, so the common case is a ref read and a comparison. */
  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (intent) return;
    /* Mouse and pen only. A touch drag over the demo is the visitor SCROLLING
       the page, not inspecting the tab, and granting a pause for it would fight
       the touch rule below — which already stops rotation outright on a tap. */
    if (e.pointerType === 'touch') return;
    const last = lastPointRef.current;
    const point = { x: e.clientX, y: e.clientY };
    lastPointRef.current = point;
    if (!last) return;
    travelRef.current += Math.hypot(point.x - last.x, point.y - last.y);
    if (travelRef.current >= HOVER_INTENT_PX) setIntent(true);
  };

  /* Drains the budget. `activeTab` is in the deps as well as `paused` because a
     pointer that never leaves sees `paused` stay true across a tab change —
     without it, the timer armed for the OLD tab would survive and expire early
     against the new tab's fresh budget. */
  useEffect(() => {
    if (!paused) return;
    const startedAt = Date.now();
    const t = setTimeout(() => setBudgetSpent(true), budgetRef.current);
    return () => {
      clearTimeout(t);
      budgetRef.current = Math.max(0, budgetRef.current - (Date.now() - startedAt));
    };
  }, [paused, activeTab]);

  /* setTimeout keyed on the ACTIVE tab, not a setInterval: the dwell is per-tab
     now (tabs 3 and 4 run long), and one interval cannot change its period.

     BUGFIX 2026-09-06: it used to arm `cycleFor(activeTab)` every time, so
     lifting a hover restarted the dwell from zero — "hovering over the fields
     resets the timer, not just pause it" (owner). Someone who moused over the
     mockup for two seconds to read a row got the full 6s again on the way out.
     The cleanup now DEBITS the time already spent, and the next arm is for
     whatever is left, so a pause is only ever a pause. */
  useEffect(() => {
    if (!started || paused || stopped) return;
    const startedAt = Date.now();
    const t = setTimeout(
      () => setActiveTab((i) => (i + 1) % copy.tabs.length),
      remainingRef.current
    );
    return () => {
      clearTimeout(t);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [activeTab, started, paused, stopped, copy.tabs.length]);

  /* Choosing a tab BY TOUCH stops the carousel for the rest of the visit.

     Not a device guess: `(hover: none)` is the precise condition under which
     the hover pause can never engage, so a tap is the only pause signal the
     device is able to give us. A mouse user who clicks a tab still has the
     pointer over the demo, so the (capped) hover pause already covers them —
     which is why the old always-on `userManual` flag was both redundant and
     wrong on desktop.

     Uncapped on purpose, unlike hover: a tap is someone actively choosing a
     tab, not someone drifting, so moving them off it 10s later would override
     an explicit instruction. */
  const handleSelect = (i: number) => {
    setActiveTab(i);
    if (window.matchMedia?.('(hover: none)').matches) setStopped(true);
  };

  /* All five are built; the fallback is here only to satisfy the index lookup
     under noUncheckedIndexedAccess, and would show tab 1 rather than an empty
     frame if the copy modules and TAB_VIEWS ever fell out of step on length. */
  const tabView = TAB_VIEWS[activeTab] ?? MINUTES_VIEW;

  return (
    /* dir is set explicitly rather than inherited from <html> so the AR prose
       rules in Hero.css can anchor to the section itself — same pattern as
       FeaturesSection and the Legal/Trust pages, and the reason an English
       fallback under a still-RTL <html> never picks up AR justification. */
    <section className="mkt-hero-tw" dir={dir}>
      <div className="mkt-hero-tw__halo" aria-hidden="true" />
      <div className="mkt-hero-tw__inner">
        {locale === 'ar' ? (
          <TypewriterAR words={PRI_WORDS} prefix="Pri" />
        ) : (
          <Typewriter words={PRI_WORDS} prefix="Pri" />
        )}
        <Waveform variant="voice-b" />
        <h1 className="mkt-hero-tw__lead">{copy.lead}</h1>
        <p className="mkt-hero-tw__sub">{copy.sub}</p>
        <div className="mkt-hero-tw__ctas">
          <a
            className="mkt-hero-tw__cta mkt-hero-tw__cta--wide"
            href={appSignupUrl(locale)}
          >
            {copy.cta}
          </a>
        </div>
        <p className="mkt-hero-tw__cta-note">{copy.ctaNote}</p>
      </div>

      {/* The disclosure band used to render HERE, between the CTA and the demo.
          It now follows the demo, and is composed in routes/index.tsx rather
          than inside this section — see the note there. */}
      {SHOW_HERO_DEMO && (
        /* BUGFIX 2026-09-05: the hover handlers were on a div wrapping only
           TabRow, so the cycle paused over the tabs but kept running over the
           shell — a visitor reading the minutes got yanked to the next tab
           mid-sentence. They belong on the whole demo block: the shell is the
           part people actually dwell on. */
        <div
          className="mkt-hero-tw__inner mkt-hero-tw__inner--demo"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => {
            setHovered(false);
            /* The path resets but `intent` does NOT — a tab already earned
               stays earned. Clearing the last point matters though: without it
               the next entry measures a jump from wherever the pointer left,
               which is a long straight line the visitor never drew, and would
               grant the budget on re-entry alone. */
            lastPointRef.current = null;
          }}
          onPointerMove={handlePointerMove}
        >
          <TabRow
            tabs={copy.tabs}
            activeIndex={activeTab}
            onSelect={handleSelect}
            showProgress
            /* `stopped` reads as paused too, and honestly so: rotation really
               has stopped, and the bar saying otherwise would be the lie. */
            paused={paused || stopped}
            /* SEPARATE from `paused` on purpose. Before the demo is reached the
               bar must not run — it would count down a dwell that is not
               elapsing — but it must not show the PAUSE BADGE either: nothing
               has been paused, it has not begun. `started` freezes the fill and
               the badge stays tied to a real pause. */
            started={started}
            cycleMs={cycleFor(activeTab)}
          />
          {/* Only the ACTIVE tab's view is mounted, which is why the Arabic in
              TranscriptView costs nothing until a visitor reaches tab 2: it is
              absent from the prerendered HTML, and the Plex Sans Arabic woff2
              (~42KB) is only fetched when those glyphs enter the DOM.
              It is also what makes each tab's scripted sequence start when the
              visitor arrives on it rather than on page load.
              All five tabs are built and bilingual as of 2026-09-07; the shell
              takes the page's locale, and every view reads it back off
              AppShell's context rather than off the route. */}
          {/* The stage exists ONLY to give the nav buttons something to anchor
              to. They cannot hang off .pnshell-frame itself: that declares the
              container query the whole shell layout keys on, and an absolutely
              positioned child of it would be measured against a box the app
              chrome is already using for its own breakpoints. A plain wrapper
              keeps the two concerns apart. */}
          <div className="mkt-hero-tw__stage" ref={stageRef}>
            {tabView.kind === 'recorder' ? (
              <RecordingShell locale={locale} nav={tabView.nav}>
                {tabView.view}
              </RecordingShell>
            ) : tabView.kind === 'actions' ? (
              <ActionsShell locale={locale} nav={tabView.nav}>
                {tabView.view}
              </ActionsShell>
            ) : tabView.kind === 'library' ? (
              <LibraryShell locale={locale} nav={tabView.nav}>
                {tabView.view}
              </LibraryShell>
            ) : (
              <AppShell
                locale={locale}
                activeNavItem={tabView.nav}
                activeSection={tabView.section}
                header={tabView.header[locale]}
              >
                {tabView.view}
              </AppShell>
            )}
            {/* Goes through handleSelect, not setActiveTab, so pressing an
                arrow behaves exactly like pressing a tab — including the touch
                rule that stops rotation for the rest of the visit. A second
                path into the same state is how the two drift apart. */}
            <DemoNav
              prevLabel={copy.navPrev}
              nextLabel={copy.navNext}
              activeIndex={activeTab}
              count={copy.tabs.length}
              onStep={handleSelect}
            />
          </div>
        </div>
      )}
    </section>
  );
}
